import {
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import JsonSchemaForm from 'react-json-schema-form';
import RadDialog from '../../RadDialog';

const ModalsContainer = ({
  modalState,
  setModalState,
  nodes,
  onSubmitForm,
  onDeployConfirmed,
}) => {
  const {
    isModalOpen,
    missingConnectionsModalOpen,
    selectedNodeId,
    formData,
    missingConnections,
    schema,
    deployPlanOpen,
    deployPlanLoading,
    deployPlan,
  } = modalState;

  const formRef = useRef(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleCloseModal = useCallback(() => {
    setModalState((prev) => ({
      ...prev,
      isModalOpen: false,
      selectedNodeId: null,
      formData: {},
    }));
  }, [setModalState]);

  const handleSubmitForm = useCallback(
    async (formData) => {
      if (selectedNodeId && onSubmitForm) {
        try {
          setIsUpdating(true);
          const node = nodes.find((n) => n.id === selectedNodeId);
          if (node && node.data.updateNodeData) {
            node.data.updateNodeData({ isUpdating: true });
          }
          handleCloseModal();
          await onSubmitForm(selectedNodeId, formData);
        } catch (error) {
          console.error('Error submitting form:', error);
        } finally {
          setIsUpdating(false);
          const node = nodes.find((n) => n.id === selectedNodeId);
          if (node && node.data.updateNodeData) {
            node.data.updateNodeData({ isUpdating: false });
          }
        }
      }
    },
    [selectedNodeId, onSubmitForm, handleCloseModal, nodes]
  );

  // Explicit user confirmation — bypasses validation/preview on purpose.
  const handleDeployWithMissingConnections = useCallback(async () => {
    if (selectedNodeId && onDeployConfirmed) {
      setModalState((prev) => ({
        ...prev,
        missingConnectionsModalOpen: false,
      }));
      await onDeployConfirmed(selectedNodeId);
    }
  }, [selectedNodeId, onDeployConfirmed, setModalState]);

  const closeDeployPlan = useCallback(() => {
    setModalState((prev) => ({
      ...prev,
      deployPlanOpen: false,
      deployPlan: null,
      deployPlanLoading: false,
    }));
  }, [setModalState]);

  const confirmDeployPlan = useCallback(async () => {
    if (!selectedNodeId || !onDeployConfirmed) return;
    closeDeployPlan();
    await onDeployConfirmed(selectedNodeId);
  }, [selectedNodeId, onDeployConfirmed, closeDeployPlan]);

  // const closeNameModal = useCallback(() => {
  //   setModalState((prev) => ({
  //     ...prev,
  //     // isNameModalOpen: false,
  //     droppedPackageInfo: null,
  //   }));
  // }, [setModalState]);

  const closeMissingConnectionsModal = useCallback(() => {
    setModalState((prev) => ({
      ...prev,
      missingConnectionsModalOpen: false,
    }));
  }, [setModalState]);

  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedNodeId),
    [nodes, selectedNodeId]
  );

  return (
    <>
      <RadDialog
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={selectedNode ? selectedNode.data.label : ''}
        maxWidth="md"
        actions={
          <>
            <Button onClick={handleCloseModal} color="primary">
              Cancel
            </Button>
            <Button
              onClick={() => formRef.current?.submit()}
              color="primary"
              variant="contained"
              disabled={isUpdating}
            >
              {isUpdating ? 'Updating...' : 'Submit'}
            </Button>
          </>
        }
      >
        <JsonSchemaForm
          key={isModalOpen && selectedNodeId ? selectedNodeId : 'package-form'}
          ref={formRef}
          schema={schema}
          initialData={formData}
          onSubmit={handleSubmitForm}
          hideSubmitButton={true}
        />
      </RadDialog>
      {/* <CreatePackageModal
        open={isNameModalOpen}
        onClose={closeNameModal}
        onSubmit={handleNameSubmit}
      /> */}
      <RadDialog
        open={missingConnectionsModalOpen}
        onClose={closeMissingConnectionsModal}
      >
        <DialogTitle>Missing Required Connections</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            These required inputs are not connected — the deploy will run
            without them:
          </Typography>
          <List dense>
            {missingConnections.map((input) => (
              <ListItem key={input}>
                <ListItemText primary={input} />
              </ListItem>
            ))}
          </List>
          <Typography variant="body2" color="text.secondary">
            Connect them first for a complete deploy; deploying anyway may fail
            or produce partial infrastructure.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMissingConnectionsModal}>Cancel</Button>
          <Button onClick={handleDeployWithMissingConnections} color="primary">
            Deploy Anyway
          </Button>
        </DialogActions>
      </RadDialog>
      {/* Deploy plan — dry-run preview, same order/resolver as the real run */}
      <DeployPlanDialog
        open={deployPlanOpen}
        loading={deployPlanLoading}
        plan={deployPlan}
        nodeLabel={selectedNode?.data?.label || ''}
        onClose={closeDeployPlan}
        onConfirm={confirmDeployPlan}
      />
    </>
  );
};

/**
 * Deploy plan preview — the ghost is the contract. Renders the exact
 * topological order the deploy will run (server dry-run, same resolver),
 * marks which packages will actually execute vs. skip, and prices the
 * commitment button with that count before anything is enqueued.
 */
const DeployPlanDialog = ({ open, loading, plan, nodeLabel, onClose, onConfirm }) => {
  const planItems = useMemo(() => plan?.plan || [], [plan]);
  const wouldEnqueue = useMemo(
    () => new Set(plan?.would_enqueue || []),
    [plan]
  );
  const runCount = wouldEnqueue.size || planItems.filter((p) => !p.skipped).length;

  return (
    <RadDialog
      isOpen={open}
      onClose={onClose}
      title={`Deploy plan · ${nodeLabel}`}
      maxWidth="sm"
      actions={
        <>
          <Button onClick={onClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            color="primary"
            variant="contained"
            disabled={loading || !plan || runCount === 0}
          >
            {loading
              ? 'Planning…'
              : plan
                ? `Deploy ${runCount} package${runCount === 1 ? '' : 's'}`
                : 'Deploy'}
          </Button>
        </>
      }
    >
      {loading || !plan ? (
        <Typography variant="body2" color="text.secondary">
          Computing the deploy order (dry-run — nothing runs yet)…
        </Typography>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Dry-run verified against the live graph — this is the exact order
            the deploy executes:
          </Typography>
          <List dense disablePadding>
            {planItems.map((item, idx) => {
              const runs = wouldEnqueue.has(item.package_id) || item.skipped === false;
              return (
                <ListItem key={item.package_id} disableGutters>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      width: '100%',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ minWidth: 20, color: 'text.secondary' }}
                    >
                      {idx + 1}.
                    </Typography>
                    <ListItemText
                      primary={item.name || item.package_id}
                      secondary={
                        item.skipped && item.skip_reason
                          ? `skip: ${item.skip_reason}`
                          : item.deploy_status
                      }
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={runs ? 'will run' : 'skip'}
                      color={runs ? 'primary' : 'default'}
                    />
                  </Box>
                </ListItem>
              );
            })}
          </List>
          {plan.note ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 1 }}
            >
              {plan.note}
            </Typography>
          ) : null}
        </>
      )}
    </RadDialog>
  );
};

export default React.memo(ModalsContainer);
